import { formatMeter } from "@core/helpers"
import {
    OPTIM_TURPE_DASHBOARD_ANALYSIS_SUCCESS,
    OPTIM_TURPE_DASHBOARD_SEGMENT_FILTER_GROUPS,
} from "@modules/courtage/constants/optim-turpe"
import type {
    OptimTurpeDashboardAnalyseItem,
    OptimTurpeDashboardSyntheseStats,
    OptimTurpeDashboardTableRow,
    OptimTurpeDashboardWorkflowAnalysesResponse,
} from "@modules/courtage/types/optim-turpe-dashboard"

/**
 * Lit une statistique KPI à la racine ou sous `synthese` (mock / API).
 */
export function getOptimTurpeDashboardStat(
    synthesis: OptimTurpeDashboardWorkflowAnalysesResponse | null,
    key: keyof OptimTurpeDashboardSyntheseStats
): number | undefined {
    if (!synthesis) return undefined
    const root = (synthesis as OptimTurpeDashboardSyntheseStats)[key]
    if (typeof root === "number") return root
    const nested = synthesis.synthese?.[key]
    return typeof nested === "number" ? nested : undefined
}

/**
 * Détermine la clé de filtre segment (C5/C4/C2) pour une valeur segment compteur.
 */
export function getOptimTurpeDashboardSegmentFilterKey(
    segment: string | undefined
): string {
    if (!segment) return ""
    const entries = Object.entries(OPTIM_TURPE_DASHBOARD_SEGMENT_FILTER_GROUPS)
    for (const [key, group] of entries) {
        if (group.includes(segment)) return key
    }
    return segment
}

/**
 * Construit l’URL SPA depuis `inputs.url_app` (legacy dashboard).
 */
export function resolveOptimTurpeDashboardRowUrl(
    analyse: OptimTurpeDashboardAnalyseItem
): string | undefined {
    const urlApp = analyse.inputs?.url_app
    const sourceId = analyse.inputs?._id_source
    if (!urlApp) return undefined
    if (urlApp.includes("/source/")) {
        const id = urlApp.split("/source/").pop()?.split("/")?.[0]
        return id ? `/source/${id}` : undefined
    }
    if (urlApp.includes("/building/")) {
        const id = urlApp.split("/building/").pop()?.split("/")?.[0]
        if (!id || !sourceId) return undefined
        return `/building/${id}/optimisation-contractuelle?idsource=${sourceId}`
    }
    return undefined
}

/**
 * Transforme la liste `analyses` en lignes tableau (succès uniquement).
 */
export function buildOptimTurpeDashboardTableRows(
    synthesis: OptimTurpeDashboardWorkflowAnalysesResponse | null
): OptimTurpeDashboardTableRow[] {
    if (!synthesis?.analyses?.length) return []
    return synthesis.analyses
        .filter((a) => a.status === OPTIM_TURPE_DASHBOARD_ANALYSIS_SUCCESS)
        .map((analyse) => {
            const idsource = analyse.data?.idsource?.value ?? ""
            const segment = analyse.data?.segment?.value ?? ""
            const economy =
                Number(analyse.data?.economies_positives_avec_cta?.value) || 0
            const sourceId = analyse.inputs?._id_source ?? ""
            return {
                _id: sourceId || idsource,
                idsource,
                idsourceDisplay: formatMeter(idsource),
                segment,
                segmentFilterKey:
                    getOptimTurpeDashboardSegmentFilterKey(segment),
                address: analyse.data?.address?.value ?? "",
                economy,
                economyDisplay: economy.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }),
                url: resolveOptimTurpeDashboardRowUrl(analyse),
            }
        })
}

/**
 * Filtre les lignes par segments sélectionnés (clés C5/C4/C2).
 */
export function filterOptimTurpeDashboardRowsBySegmentKeys(
    rows: OptimTurpeDashboardTableRow[],
    selectedKeys: string[]
): OptimTurpeDashboardTableRow[] {
    if (!selectedKeys.length) return rows
    return rows.filter((row) =>
        selectedKeys.some((key) => {
            const group = OPTIM_TURPE_DASHBOARD_SEGMENT_FILTER_GROUPS[key]
            return group ? group.includes(row.segment) : row.segment === key
        })
    )
}

/**
 * Filtre texte sur adresse, idsource, segment.
 */
export function filterOptimTurpeDashboardRowsBySearch(
    rows: OptimTurpeDashboardTableRow[],
    search: string
): OptimTurpeDashboardTableRow[] {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => {
        const hay = [
            row.address,
            row.idsource,
            row.segment,
            row.idsourceDisplay,
        ]
            .join(" ")
            .toLowerCase()
        return hay.includes(q)
    })
}

/**
 * Trie par montant d’économie.
 */
export function sortOptimTurpeDashboardRowsByEconomy(
    rows: OptimTurpeDashboardTableRow[],
    order: "asc" | "desc"
): OptimTurpeDashboardTableRow[] {
    const copy = [...rows]
    copy.sort((a, b) =>
        order === "asc" ? a.economy - b.economy : b.economy - a.economy
    )
    return copy
}
